import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createApplicationCategory, fetchApplicationTypes, fetchBusinessUnitsByDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function ApplicationCategoryCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [applicationTypes, setApplicationTypes] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [form, setForm] = useState({
    applicationCategoryName: "",
    applicationCategoryDescription: "",
    url: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [types, units] = await Promise.all([
          fetchApplicationTypes(businessDomainId),
          fetchBusinessUnitsByDomain(businessDomainId),
        ]);
        setApplicationTypes(Array.isArray(types) ? types : []);
        if (Array.isArray(units) && units.length > 0) {
          setBusinessUnitId(units[0].businessUnitId);
        }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const associatedApplicationTypes = selectedTypeIds.map((id) => {
      const found = applicationTypes.find((t) => t.applicationTypeId === id);
      return { applicationTypeId: id, applicationTypeName: found?.applicationTypeName || "" };
    });
    try {
      await createApplicationCategory({
        companyId: COMPANY_ID,
        businessDomainId,
        businessUnitId,
        applicationCategoryName: form.applicationCategoryName,
        applicationCategoryDescription: form.applicationCategoryDescription || null,
        url: form.url || null,
        associatedApplicationTypes,
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/application-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("applicationCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("applicationCategory.applicationType")}</InputLabel>
              <Select
                multiple
                value={selectedTypeIds}
                onChange={(e) => setSelectedTypeIds(e.target.value)}
                input={<OutlinedInput label={t("applicationCategory.applicationType")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const found = applicationTypes.find((t) => t.applicationTypeId === id);
                      return <Chip key={id} label={found?.applicationTypeName || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {applicationTypes.map((at) => (
                  <MenuItem key={at.applicationTypeId} value={at.applicationTypeId}>
                    {at.applicationTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("applicationCategory.name")}
              name="applicationCategoryName"
              value={form.applicationCategoryName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("common.description")}
              name="applicationCategoryDescription"
              value={form.applicationCategoryDescription}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/application-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
